import { useEffect, useState } from 'react';

import { Box, DropdownSelect, Text, ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';

import { useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useGetPropertiesData, useSpotlightOverview } from '../../../hooks/queries/properties/zram';
import { useScreenSize } from '../../../hooks/useScreenSize';
import accessUtils from '../../../utils/accessUtils';

import PropertiesConstants from '../../../constants/PropertiesConstants';
import PropertyConstants from '../../../constants/PropertyConstants';

import { useAuth } from '../../../components/AuthProvider';
import { ControlledSearchBox } from '../../../components/ControlledSearchBox';

import { filterStyles } from '../styles';

import {
  emitPropertyListFilterEvent,
  EVENT_IDS as PROPERTIES_PAGE_EVENT_IDS,
} from '../../../analytics/events/propertiesPage';

import type { Dispatch, SetStateAction } from 'react';
import type { FilterState } from '../../../utils/propertiesPageUtils';

const { PROPERTIES_SUBNAV_FILTER_TYPE } = PropertiesConstants;
const { PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

export const Filters = ({
  filters,
  setFilters,
}: {
  filters: FilterState;
  setFilters: Dispatch<SetStateAction<FilterState>>;
}) => {
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const [searchInput, setSearchInput] = useState(filters.searchText);

  const { data: { listingCount } = {}, isPending: isPropertiesPending } = useGetPropertiesData(filters);
  const { data: spotlightOverviewData } = useSpotlightOverview();
  const canAccessSpotlightFilter = accessUtils.canAccessListingSpotlightFilter({
    user,
    spotlightOverview: spotlightOverviewData,
  });
  const { data: { listingCount: spotlightListingCount } = {} } = useGetPropertiesData(
    {
      ...filters,
      subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.SPOTLIGHT,
    },
    { enabled: canAccessSpotlightFilter },
  );

  const { data: { unitProperties: claimableProperties = [] } = {} } =
    useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties(
      {},
      {
        enabled: user?.features?.canClaimFreeListings ?? false,
      },
    );

  const isNonAdvertiser = accessUtils.isNonAdvertiser({ user });
  const canAccessClaimPath = accessUtils.canAccessClaimPath({
    isScreenMobile,
    hasAnyPropertiesToClaim: claimableProperties.length > 0,
    isNonAdvertiser,
  });

  const {
    total: numTotalListings = 0,
    freeClaimedListingCount: numClaimedListings = 0,
    active: numActiveListings = 0,
    inactive: numInactiveListings = 0,
    atRisk: numAtRiskListings = 0,
    freeBonusListingCount: numBonusListings = 0,
  } = listingCount || {};

  const showClaimedTab = numClaimedListings > 0 || canAccessClaimPath;
  const showBonusTab = numBonusListings > 0;
  const showAtRiskTab = numAtRiskListings > 0;
  const showSpotlightTab = canAccessSpotlightFilter;
  const numSpotlightListings = spotlightListingCount?.total ?? 0;

  // Sync searchInput with filters.searchText when it changes from external sources
  useEffect(() => {
    setSearchInput(filters.searchText);
  }, [filters.searchText]);

  useEffect(() => {
    // if screen is mobile, hide "Claimed" tab and redirect to "For rent" tab
    if (filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED && !showClaimedTab) {
      setFilters((currentFilters) => ({
        ...currentFilters,
        subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET,
      }));
    }
  }, [showClaimedTab, filters.subNavFilterType]);

  const renderToggleButton = (value: string, label: string, count: number) => {
    return (
      <ToggleButton
        value={value}
        css={{ xlDown: { width: '100%' } }}
        data-eventid={PROPERTIES_PAGE_EVENT_IDS.PROPERTY_LIST_FILTER}
        data-testid={`PropertiesPage-Filters-${value}`}
      >
        {label} {listingCount && <Text textStyle="body-sm-bold">{`(${count})`}</Text>}
      </ToggleButton>
    );
  };

  const onFilterChange = (value: string | Array<string>) => {
    const subNavFilterType = Array.isArray(value) ? value[0] : value;

    setFilters((currentFilters) => ({ ...currentFilters, subNavFilterType }));
    emitPropertyListFilterEvent({
      user,
      triggerObject:
        PROPERTY_TABLE_TRIGGER_OBJECT_MAP.FILTER_TRIGGERS[
          subNavFilterType as keyof typeof PROPERTY_TABLE_TRIGGER_OBJECT_MAP.FILTER_TRIGGERS
        ],
    });
  };

  const onSearchInputChange = (value: string) => {
    // Immediately change input value for UI feedback
    setSearchInput(value);
  };

  const onSearch = (value: string) => {
    // Only set filters after debounce to prevent excessive API calls
    setFilters((currentFilters) => ({ ...currentFilters, searchText: value }));
  };

  return (
    <Box css={filterStyles}>
      {/* Shown on lg screens and above */}
      <ToggleButtonGroup
        aria-label="Filter property list by status"
        size="sm"
        css={{ xlDown: { width: '100%' }, display: { base: 'none', lg: 'flex' } }}
        onValueChange={onFilterChange}
        value={filters.subNavFilterType}
        data-testid="PropertiesPage-Filters-AllButton"
      >
        {renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.ALL, 'All', numTotalListings)}
        {renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET, 'For rent', numActiveListings)}
        {renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET, 'Off market', numInactiveListings)}
        {showClaimedTab && renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED, 'Claimed', numClaimedListings)}
        {showBonusTab &&
          renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS, 'Bonus', numBonusListings)}
        {showSpotlightTab &&
          renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.SPOTLIGHT, 'Spotlight', numSpotlightListings)}
        {showAtRiskTab && renderToggleButton(PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK, 'At risk', numAtRiskListings)}
      </ToggleButtonGroup>
      {/* Shown on md screens and below */}
      <DropdownSelect
        key={`${filters.subNavFilterType}-${numTotalListings}-${numActiveListings}-${numInactiveListings}`}
        value={filters.subNavFilterType}
        options={[
          { label: `All (${numTotalListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.ALL },
          { label: `For rent (${numActiveListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET },
          { label: `Off market (${numInactiveListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET },
          ...(showClaimedTab
            ? [{ label: `Claimed (${numClaimedListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED }]
            : []),
          ...(showBonusTab
            ? [{ label: `Bonus (${numBonusListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS }]
            : []),
          ...(showSpotlightTab
            ? [{ label: `Spotlight (${numSpotlightListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.SPOTLIGHT }]
            : []),
          ...(showAtRiskTab
            ? [{ label: `At risk (${numAtRiskListings})`, value: PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK }]
            : []),
        ]}
        showLabelForValue
        css={{ width: '100%', display: { base: 'flex', lg: 'none' } }}
        onChange={onFilterChange}
        data-eventid={PROPERTIES_PAGE_EVENT_IDS.PROPERTY_LIST_FILTER}
        size="sm"
      />
      <Box css={{ width: { base: '100%', xl: '50%' } }}>
        <ControlledSearchBox
          placeholder="Search by property name or address"
          value={searchInput}
          onChange={onSearchInputChange}
          onSearch={onSearch}
          isSearching={isPropertiesPending}
          iconSize="sm"
        />
      </Box>
    </Box>
  );
};
